import { useEffect, useRef, useState } from 'react';
import { Loader2, Plus, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useCompanySearch } from '@/api/hooks';
import { CreateCompanyDialog } from '@/components/CreateCompanyDialog';
import { FloatingPanel } from '@/components/FloatingPanel';

export interface CompanyOption {
  id: number;
  name: string;
}

interface Props {
  value: CompanyOption | null;
  onChange: (val: CompanyOption | null) => void;
  placeholder?: string;
  className?: string;
  /** When true, offers a "Create new company" action when the search has no match. */
  allowCreate?: boolean;
}

export function CompanySearch({ value, onChange, placeholder = 'Search company…', className, allowCreate = false }: Props) {
  const [text, setText] = useState(value?.name ?? '');
  const [open, setOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  // The results list is portalled (FloatingPanel) so table/card overflow can't clip it.
  const panelRef = useRef<HTMLDivElement>(null);
  const results = useCompanySearch(text);

  useEffect(() => {
    setText(value?.name ?? '');
  }, [value?.name]);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (containerRef.current && !containerRef.current.contains(t) && !panelRef.current?.contains(t)) {
        setOpen(false);
        // Reset input to currently selected value if user typed but didn't pick
        setText(value?.name ?? '');
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open, value?.name]);

  const select = (c: { id: number; companyName: string }) => {
    onChange({ id: c.id, name: c.companyName });
    setText(c.companyName);
    setOpen(false);
  };

  const clear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
    setText('');
    setOpen(false);
  };

  const startCreate = () => {
    setOpen(false);
    setCreateOpen(true);
  };

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <div className="relative">
        <Input
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setOpen(true);
            if (!e.target.value) onChange(null);
          }}
          onFocus={() => { if (text.trim().length >= 2) setOpen(true); }}
          placeholder={placeholder}
          className={cn('pr-7', value && 'font-medium')}
          autoComplete="off"
        />
        {value && (
          <button
            type="button"
            onClick={clear}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded text-muted-foreground hover:text-foreground"
            aria-label="Clear"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {open && text.trim().length >= 2 && (
        <FloatingPanel ref={panelRef} anchor={containerRef.current} maxHeight={224}>
          {results.isFetching && (
            <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />Searching…
            </div>
          )}
          {!results.isFetching && (results.data?.items ?? []).length === 0 && (
            allowCreate ? (
              <button
                type="button"
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-brand-strong hover:bg-accent dark:text-brand"
                onMouseDown={(e) => { e.preventDefault(); startCreate(); }}
              >
                <Plus className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">Create “{text.trim()}” <span className="text-muted-foreground">(new)</span></span>
              </button>
            ) : (
              <div className="px-3 py-2 text-sm text-muted-foreground">No companies found</div>
            )
          )}
          {(results.data?.items ?? []).map((c) => (
            <button
              key={c.id}
              type="button"
              className="w-full px-3 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground"
              onMouseDown={(e) => { e.preventDefault(); select(c); }}
            >
              {c.companyName}
            </button>
          ))}
          {allowCreate && !results.isFetching && (results.data?.items ?? []).length > 0 && (
            <button
              type="button"
              className="mt-1 flex w-full items-center gap-2 border-t border-border px-3 py-2 text-left text-sm text-brand-strong hover:bg-accent dark:text-brand"
              onMouseDown={(e) => { e.preventDefault(); startCreate(); }}
            >
              <Plus className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">Create “{text.trim()}” <span className="text-muted-foreground">(new)</span></span>
            </button>
          )}
        </FloatingPanel>
      )}
      {allowCreate && (
        <CreateCompanyDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          initialName={text.trim()}
          onCreated={(c) => { onChange(c); setText(c.name); }}
        />
      )}
    </div>
  );
}
